import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, ChevronLeft, ChevronRight, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicPromoLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [schengenCountries, setSchengenCountries] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [showSchengenSelector, setShowSchengenSelector] = useState(false);
  const [showOtherCountryInput, setShowOtherCountryInput] = useState(false);
  const [countryError, setCountryError] = useState('');
  const scrollContainerRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    destination_country: '',
    schengen_country: '',
    other_country: '',
    num_applicants: 1,
    message: ''
  });

  useEffect(() => {
    fetchPromoData();
    fetchServices();
    fetchCountries();
    fetchSchengenCountries();
    fetchTestimonials();
  }, [slug]);

  const fetchPromoData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/promo/${slug}`);
      setAnnouncement(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch promo:', error);
      setLoading(false);
      // Redirect to home after 3 seconds
      setTimeout(() => navigate('/'), 3000);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/countries`);
      setCountries(response.data || []);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchSchengenCountries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/schengen-countries`);
      setSchengenCountries(response.data || []);
    } catch (error) {
      console.error('Failed to fetch Schengen countries:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
      setTestimonials(response.data || []);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const validateCountryName = async (countryName) => {
    if (!countryName || !countryName.trim()) {
      return { valid: false, message: 'Country name is required' };
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/validate-country`, {
        name: countryName.trim()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to validate country:', error);
      return { valid: false, message: 'Failed to validate country name' };
    }
  };

  const handleOtherCountryBlur = async () => {
    if (formData.other_country && formData.other_country.trim()) {
      const validation = await validateCountryName(formData.other_country);
      if (!validation.valid) {
        setCountryError(validation.message || 'Please enter a valid country name');
      } else {
        setCountryError('');
        // Update with normalized name if provided
        if (validation.normalized_name) {
          setFormData({ ...formData, other_country: validation.normalized_name });
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate "Other" country if selected
    if (formData.destination_country === 'Other' && formData.other_country) {
      const validation = await validateCountryName(formData.other_country);
      if (!validation.valid) {
        setCountryError(validation.message || 'Please enter a valid country name');
        setSubmitting(false);
        alert(validation.message || 'Please enter a valid country name');
        return;
      }
      // Use normalized name
      if (validation.normalized_name) {
        formData.other_country = validation.normalized_name;
      }
    }

    try {
      await axios.post(`${BACKEND_URL}/api/public/promo/${slug}/submit`, formData);
      setSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        destination_country: '',
        num_applicants: 1,
        message: ''
      });

      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to submit form:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <Loader2 size={48} className="animate-spin" style={{ color: '#8b5cf6' }} />
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  if (!announcement) {
    return (
      <div style={styles.errorContainer}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc2626', marginBottom: '1rem' }}>
          Promo Not Found
        </h1>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
          This promotional page is not available or has expired.
        </p>
        <button
          onClick={() => navigate('/')}
          style={styles.errorButton}
        >
          Go to Homepage
        </button>
      </div>
    );
  }

  const themeColors = {
    professional: { primary: '#3b82f6', secondary: '#1e40af', bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    colorful: { primary: '#ec4899', secondary: '#9333ea', bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
    festive: { primary: '#f59e0b', secondary: '#dc2626', bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' },
    custom: { 
      primary: announcement.background_color || '#8b5cf6', 
      secondary: announcement.text_color || '#ffffff',
      bg: `linear-gradient(135deg, ${announcement.background_color || '#8b5cf6'} 0%, ${announcement.text_color || '#ffffff'} 100%)`
    }
  };

  const theme = themeColors[announcement.theme] || themeColors.professional;

  return (
    <div style={styles.container}>
      {/* Success Message */}
      {success && (
        <div style={styles.successBanner}>
          <CheckCircle2 size={24} style={{ color: '#10b981' }} />
          <div>
            <h3 style={{ fontWeight: '600', color: '#065f46' }}>Thank You!</h3>
            <p style={{ fontSize: '0.875rem', color: '#047857' }}>
              We've received your information and will contact you soon.
            </p>
          </div>
        </div>
      )}

      {/* Testimonials Carousel */}
      {testimonials.length > 0 && (
        <div style={styles.testimonialsSection}>
          <div style={styles.carouselContainer}>
            <button 
              onClick={() => scroll('left')} 
              style={styles.scrollButton}
              aria-label="Scroll left"
            >
              <ChevronLeft size={24} />
            </button>
            
            <div ref={scrollContainerRef} style={styles.testimonialsList} className="testimonials-carousel">
              {testimonials.map((testimonial, index) => (
                <div key={index} style={styles.testimonialCard}>
                  <div style={styles.stars}>
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} size={16} fill="#fbbf24" color="#fbbf24" />
                    ))}
                  </div>
                  <p style={styles.testimonialText}>"{testimonial.text}"</p>
                  <div style={styles.testimonialAuthor}>
                    <strong>{testimonial.name}</strong>
                    {testimonial.designation && (
                      <span style={styles.designation}> - {testimonial.designation}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              onClick={() => scroll('right')} 
              style={styles.scrollButton}
              aria-label="Scroll right"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
      )}

      {/* Lead Capture Form */}
      <div style={styles.formSection}>
        <div style={styles.formContainer}>
          <h2 style={styles.formTitle}>{announcement.title}</h2>
          <p style={styles.formSubtitle}>
            Fill out the form below and our team will contact you shortly
          </p>

          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  style={styles.input}
                  placeholder="John Doe"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  style={styles.input}
                  placeholder="john@example.com"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  style={styles.input}
                  placeholder="+1 234 567 8900"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Service</label>
                <select
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  style={styles.input}
                >
                  <option value="">Select a service</option>
                  {services.map(service => (
                    <option key={service.id} value={service.title}>{service.title}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Destination Country</label>
                <select
                  value={formData.destination_country}
                  onChange={(e) => {
                    const selectedCountry = e.target.value;
                    setFormData({ 
                      ...formData, 
                      destination_country: selectedCountry,
                      schengen_country: selectedCountry === 'Schengen (Europe)' ? formData.schengen_country : '',
                      other_country: selectedCountry === 'Other' ? formData.other_country : ''
                    });
                    setShowSchengenSelector(selectedCountry === 'Schengen (Europe)');
                    setShowOtherCountryInput(selectedCountry === 'Other');
                  }}
                  style={styles.input}
                >
                  <option value="">Select a country</option>
                  {countries.map((country, index) => (
                    <option key={country.name || index} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Schengen Country Selector */}
              {showSchengenSelector && (
                <div style={{
                  ...styles.formGroup,
                  background: '#f0f9ff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #bae6fd'
                }}>
                  <label style={styles.label}>Select Specific Schengen Country *</label>
                  <select
                    value={formData.schengen_country || ''}
                    onChange={(e) => setFormData({ ...formData, schengen_country: e.target.value })}
                    required
                    style={{...styles.input, background: 'white'}}
                  >
                    <option value="">Select Schengen Country</option>
                    {schengenCountries.map(country => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#0369a1', fontSize: '0.75rem' }}>
                    💡 Select the primary destination country within Schengen area
                  </small>
                </div>
              )}

              {/* Other Country Input */}
              {showOtherCountryInput && (
                <div style={{
                  ...styles.formGroup,
                  background: '#fef3c7',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #fcd34d'
                }}>
                  <label style={styles.label}>Specify Country Name *</label>
                  <input
                    type="text"
                    value={formData.other_country || ''}
                    onChange={(e) => {
                      setFormData({ ...formData, other_country: e.target.value });
                      setCountryError(''); // Clear error on typing
                    }}
                    onBlur={handleOtherCountryBlur}
                    placeholder="Enter country name (e.g., Vietnam)..."
                    required
                    style={{
                      ...styles.input, 
                      background: 'white',
                      borderColor: countryError ? '#dc2626' : '#fcd34d'
                    }}
                  />
                  {countryError && (
                    <div style={{ 
                      display: 'block', 
                      marginTop: '0.5rem', 
                      color: '#dc2626', 
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      ⚠️ {countryError}
                    </div>
                  )}
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#92400e', fontSize: '0.75rem' }}>
                    💡 Enter the full country name (we'll validate it)
                  </small>
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Number of Applicants</label>
                <input
                  type="number"
                  min="1"
                  value={formData.num_applicants}
                  onChange={(e) => setFormData({ ...formData, num_applicants: parseInt(e.target.value) })}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                style={{ ...styles.input, minHeight: '100px', fontFamily: 'inherit' }}
                placeholder="Tell us about your requirements..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                ...styles.submitButton,
                backgroundColor: theme.primary,
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Inquiry'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
          © {new Date().getFullYear()} Visaasure. All rights reserved.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#ffffff'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh'
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '2rem'
  },
  errorButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer'
  },
  successBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem 2rem',
    backgroundColor: '#d1fae5',
    borderLeft: '4px solid #10b981'
  },
  testimonialsSection: {
    padding: '3rem 2rem',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb'
  },
  carouselContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    position: 'relative'
  },
  scrollButton: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '50%',
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    transition: 'all 0.2s',
    color: '#374151'
  },
  testimonialsList: {
    display: 'flex',
    gap: '1.5rem',
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    padding: '1rem 0',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch'
  },
  testimonialCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '2rem',
    minWidth: '350px',
    maxWidth: '350px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    flexShrink: 0,
    border: '1px solid #e5e7eb'
  },
  stars: {
    display: 'flex',
    gap: '0.25rem'
  },
  testimonialText: {
    fontSize: '0.9375rem',
    lineHeight: '1.6',
    color: '#374151',
    fontStyle: 'italic',
    flex: 1
  },
  testimonialAuthor: {
    fontSize: '0.875rem',
    color: '#6b7280',
    borderTop: '1px solid #f3f4f6',
    paddingTop: '0.75rem'
  },
  designation: {
    fontSize: '0.8125rem',
    color: '#9ca3af'
  },
  formSection: {
    padding: '4rem 2rem',
    backgroundColor: '#f9fafb'
  },
  formContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: 'white',
    padding: '3rem',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
  },
  formTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: '0.5rem',
    textAlign: 'center'
  },
  formSubtitle: {
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: '2rem'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151'
  },
  input: {
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.2s',
    outline: 'none'
  },
  submitButton: {
    padding: '1rem 2rem',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1.125rem',
    fontWeight: '600',
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.3s'
  },
  footer: {
    padding: '2rem',
    textAlign: 'center',
    borderTop: '1px solid #e5e7eb'
  }
};

// Add CSS to hide scrollbar
const styleTag = document.createElement('style');
styleTag.textContent = `
  .testimonials-carousel::-webkit-scrollbar {
    display: none;
  }
  .testimonials-carousel {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;
if (!document.querySelector('style[data-testimonials-carousel]')) {
  styleTag.setAttribute('data-testimonials-carousel', 'true');
  document.head.appendChild(styleTag);
}

export default PublicPromoLanding;
